import { useState, useEffect } from 'react'
import { get } from '../lib/api'

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const STATUS_COLOR = { completed: '#059669', in_progress: '#D97706', scheduled: '#4F46E5' }
const HOURS = Array.from({ length: 13 }, (_, i) => 7 + i) // 7:00 .. 19:00

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function parseYmd(s) { return new Date(s + 'T00:00:00') }
function hourOf(timeStr) { if (!timeStr) return null; const h = parseInt(timeStr.split(':')[0], 10); return isNaN(h) ? null : h }

// Outlook-style calendar. Creating a meeting delegates to the real meeting form
// via onCreateMeeting(dateStr) — same window used in the Meetings section.
export default function CalendarView({ t, isEmployer = false, onCreateMeeting }) {
  const today = new Date()
  // Installed app: month view only (week/day grids are cramped on a phone)
  const isStandalone = typeof window !== 'undefined' &&
    (window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true)
  const VIEWS = isStandalone ? ['month'] : ['month', 'week', 'day']
  const [mode, setMode] = useState('month') // month | week | day
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), today.getDate()))
  const [selected, setSelected] = useState(ymd(today))
  const [selectedTime, setSelectedTime] = useState(null) // 'HH:00' when an hour slot is picked
  const [meetings, setMeetings] = useState([])

  useEffect(() => { load() }, [])
  async function load() {
    try { const r = await get('/meetings'); setMeetings(r.meetings || []) } catch (e) { console.error(e.message) }
  }

  const byDate = {}
  for (const m of meetings) { if (m.meeting_date) (byDate[m.meeting_date] ||= []).push(m) }
  const todayStr = ymd(today)
  const sortDay = (list) => (list || []).slice().sort((a, b) => (a.meeting_time || '').localeCompare(b.meeting_time || ''))

  function shift(delta) {
    const d = new Date(cursor)
    if (mode === 'month') d.setMonth(d.getMonth() + delta)
    else if (mode === 'week') d.setDate(d.getDate() + delta * 7)
    else d.setDate(d.getDate() + delta)
    setCursor(d)
    if (mode === 'day') setSelected(ymd(d))
  }
  function goToday() {
    const d = new Date(today.getFullYear(), today.getMonth(), today.getDate())
    setCursor(d); setSelected(todayStr)
  }
  const create = (ds, time) => { if (isEmployer && onCreateMeeting) onCreateMeeting(ds, time) }
  const hourStr = (h) => `${String(h).padStart(2, '0')}:00`

  const card = { background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, boxShadow: t.shadow }

  // header label
  const weekStart = new Date(cursor); weekStart.setDate(cursor.getDate() - cursor.getDay())
  const weekDays = Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(weekStart.getDate() + i); return d })
  const headerLabel = mode === 'month'
    ? `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`
    : mode === 'week'
      ? `${weekDays[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${weekDays[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
      : cursor.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: t.text, margin: '0 0 4px', letterSpacing: '-0.5px' }}>Calendar</h1>
          <p style={{ fontSize: 14, color: t.textMuted, margin: 0 }}>{isEmployer ? 'Tap a day to select it, then tap Schedule' : 'Your meetings'}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ display: VIEWS.length > 1 ? 'flex' : 'none', background: t.input, border: `1px solid ${t.border}`, borderRadius: 9, padding: 3 }}>
            {VIEWS.map((m) => (
              <button key={m} onClick={() => setMode(m)} style={{ padding: '6px 14px', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize', fontFamily: 'Inter,system-ui', background: mode === m ? 'linear-gradient(135deg,#4F46E5,#6366F1)' : 'transparent', color: mode === m ? '#fff' : t.textSub }}>{m}</button>
            ))}
          </div>
          {/* nav group stays on one line */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap' }}>
            <button onClick={goToday} style={navBtn(t, true)}>Today</button>
            <button onClick={() => shift(-1)} style={navBtn(t)}>‹</button>
            <span style={{ fontSize: 14, fontWeight: 800, color: t.text, minWidth: 120, textAlign: 'center', flexShrink: 0 }}>{headerLabel}</span>
            <button onClick={() => shift(1)} style={navBtn(t)}>›</button>
          </div>
          {isEmployer && (
            <button onClick={() => create(selected, selectedTime)} style={{ padding: '8px 16px', background: 'linear-gradient(135deg,#4F46E5,#6366F1)', color: '#fff', border: 'none', borderRadius: 9, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter,system-ui', whiteSpace: 'nowrap' }}>
              + Schedule {parseYmd(selected).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}{selectedTime ? `, ${selectedTime}` : ''}
            </button>
          )}
        </div>
      </div>

      {mode === 'month' && (
        <div style={{ ...card, padding: '1.25rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 6 }}>
            {DOW.map((d) => <div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: t.textMuted, padding: '4px 0' }}>{d}</div>)}
            {(() => {
              const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1)
              const startDow = first.getDay()
              const dim = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate()
              const cells = []
              for (let i = 0; i < startDow; i++) cells.push(null)
              for (let d = 1; d <= dim; d++) cells.push(d)
              return cells.map((d, i) => {
                if (!d) return <div key={i} />
                const ds = ymd(new Date(cursor.getFullYear(), cursor.getMonth(), d))
                const dayMtgs = sortDay(byDate[ds])
                const isToday = ds === todayStr
                const isSel = ds === selected
                return (
                  <div key={i} onClick={() => { setSelected(ds); setSelectedTime(null) }} title={isEmployer ? 'Tap to select, then tap Schedule' : ''}
                    style={{ minHeight: 84, borderRadius: 10, padding: 6, cursor: 'pointer', border: `1px solid ${isSel ? '#4F46E5' : t.border}`, background: isSel ? (t.dark ? 'rgba(99,102,241,0.18)' : '#EEF2FF') : (isToday ? (t.dark ? 'rgba(99,102,241,0.08)' : '#F5F7FF') : 'transparent') }}>
                    <div style={{ fontSize: 12, fontWeight: isToday ? 800 : 600, color: isToday ? '#4F46E5' : t.text, marginBottom: 4 }}>{d}</div>
                    {dayMtgs.slice(0, 3).map((m) => <Pill key={m.id} m={m} />)}
                    {dayMtgs.length > 3 && <div style={{ fontSize: 9, color: t.textMuted }}>+{dayMtgs.length - 3} more</div>}
                  </div>
                )
              })
            })()}
          </div>
        </div>
      )}

      {mode === 'week' && (
        <div style={{ ...card, padding: '1rem', overflowX: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: `56px repeat(7, minmax(110px, 1fr))`, gap: 0 }}>
            <div />
            {weekDays.map((d) => {
              const ds = ymd(d); const isToday = ds === todayStr
              return (
                <div key={ds} onClick={() => { setSelected(ds); setCursor(d); setMode('day') }} style={{ textAlign: 'center', padding: '6px 0', cursor: 'pointer', borderBottom: `2px solid ${isToday ? '#4F46E5' : 'transparent'}` }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted }}>{DOW[d.getDay()]}</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: isToday ? '#4F46E5' : t.text }}>{d.getDate()}</div>
                </div>
              )
            })}
            {HOURS.map((h) => (
              <HourRow key={h} h={h} t={t} weekDays={weekDays} byDate={byDate} selected={selected} selectedTime={selectedTime} hourStr={hourStr} onSlot={(ds, hr) => { setSelected(ds); setSelectedTime(hourStr(hr)) }} isEmployer={isEmployer} />
            ))}
          </div>
        </div>
      )}

      {mode === 'day' && (
        <div style={{ ...card, padding: '1.25rem', maxWidth: 720, margin: '0 auto' }}>
          {(() => {
            const ds = ymd(cursor)
            const dayMtgs = sortDay(byDate[ds])
            return (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: t.text }}>{cursor.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })}</div>
                  {isEmployer && <button onClick={() => create(ds, ds === selected ? selectedTime : null)} style={{ fontSize: 12, fontWeight: 700, background: '#4F46E514', color: '#4F46E5', border: '1px solid #4F46E530', borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}>+ Schedule{ds === selected && selectedTime ? ` ${selectedTime}` : ''}</button>}
                </div>
                {HOURS.map((h) => {
                  const slotMtgs = dayMtgs.filter((m) => hourOf(m.meeting_time) === h)
                  const isSel = isEmployer && ds === selected && selectedTime === hourStr(h)
                  return (
                    <div key={h} onClick={isEmployer && slotMtgs.length === 0 ? () => { setSelected(ds); setSelectedTime(hourStr(h)) } : undefined}
                      title={isEmployer ? `Select ${h}:00` : ''}
                      style={{ display: 'flex', gap: 12, minHeight: 44, borderTop: `1px solid ${t.border}`, padding: '6px 0', cursor: isEmployer ? 'pointer' : 'default', background: isSel ? (t.dark ? 'rgba(99,102,241,0.18)' : '#EEF2FF') : 'transparent', boxShadow: isSel ? 'inset 0 0 0 2px #4F46E5' : 'none', borderRadius: isSel ? 8 : 0 }}>
                      <div style={{ width: 54, fontSize: 11, color: t.textMuted, flexShrink: 0 }}>{h}:00</div>
                      <div style={{ flex: 1 }}>
                        {slotMtgs.map((m) => <EventCard key={m.id} m={m} t={t} />)}
                      </div>
                    </div>
                  )
                })}
                {dayMtgs.filter((m) => hourOf(m.meeting_time) === null).length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 4 }}>No set time</div>
                    {dayMtgs.filter((m) => hourOf(m.meeting_time) === null).map((m) => <EventCard key={m.id} m={m} t={t} />)}
                  </div>
                )}
              </>
            )
          })()}
        </div>
      )}
    </div>
  )

  function Pill({ m }) {
    return (
      <div onClick={(e) => { e.stopPropagation(); setSelected(m.meeting_date); setCursor(parseYmd(m.meeting_date)); setMode('day') }}
        title={m.title || '1:1'} style={{ fontSize: 10, fontWeight: 600, color: '#fff', background: STATUS_COLOR[m.status] || '#4F46E5', borderRadius: 4, padding: '2px 5px', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}>
        {m.meeting_time ? m.meeting_time + ' ' : ''}{m.title || '1:1'}
      </div>
    )
  }
}

function HourRow({ h, t, weekDays, byDate, selected, selectedTime, hourStr, onSlot, isEmployer }) {
  return (
    <>
      <div style={{ fontSize: 11, color: t.textMuted, textAlign: 'right', paddingRight: 8, borderTop: `1px solid ${t.border}`, paddingTop: 4 }}>{h}:00</div>
      {weekDays.map((d) => {
        const ds = ymd(d)
        const mtgs = (byDate[ds] || []).filter((m) => hourOf(m.meeting_time) === h)
        const isSel = isEmployer && ds === selected && selectedTime === hourStr(h)
        return (
          <div key={ds} onClick={isEmployer && mtgs.length === 0 ? () => onSlot(ds, h) : undefined}
            title={isEmployer ? `Select ${h}:00` : ''}
            style={{ minHeight: 40, borderTop: `1px solid ${t.border}`, borderLeft: `1px solid ${t.border}`, padding: 2, cursor: isEmployer ? 'pointer' : 'default', background: isSel ? (t.dark ? 'rgba(99,102,241,0.25)' : '#E0E7FF') : 'transparent', boxShadow: isSel ? 'inset 0 0 0 2px #4F46E5' : 'none' }}>
            {mtgs.map((m) => <EventCard key={m.id} m={m} t={t} compact />)}
          </div>
        )
      })}
    </>
  )
}

function EventCard({ m, t, compact }) {
  const color = STATUS_COLOR[m.status] || '#4F46E5'
  return (
    <div style={{ background: color + '18', borderLeft: `3px solid ${color}`, borderRadius: 5, padding: compact ? '2px 4px' : '6px 8px', marginBottom: 3 }}>
      <div style={{ fontSize: compact ? 10 : 12, fontWeight: 700, color: t.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.title || '1:1'}</div>
      {!compact && m.meeting_time && <div style={{ fontSize: 11, color: t.textMuted }}>{m.meeting_time}{m.meeting_end_time ? ` – ${m.meeting_end_time}` : ''}</div>}
    </div>
  )
}

function navBtn(t, wide) {
  return { height: 34, minWidth: wide ? 56 : 34, padding: wide ? '0 12px' : 0, borderRadius: 8, border: `1px solid ${t.border}`, background: t.card, color: t.text, fontSize: wide ? 13 : 18, fontWeight: wide ? 600 : 400, cursor: 'pointer', lineHeight: 1, fontFamily: 'Inter,system-ui' }
}
